/**
 * api.js - API service for communicating with LeagueMaker backend
 */

import axios from 'axios';

// Base URL for API (uses env var in production, proxy in local dev)
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const SERVER_BASE_URL = API_BASE_URL.replace(/\/api$/, '');

// Get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Add auth header to requests
const getAuthHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Axios response interceptor — handles 401 (auto-logout) and network errors
axios.interceptors.response.use(
  response => response,
  error => {
    // Network error (no response from server)
    if (!error.response) {
      error._isNetworkError = true;
    }
    // 401 Unauthorized — token expired or invalid; auto-logout
    if (error.response?.status === 401) {
      const path = error.config?.url || '';
      // Don't auto-logout on login/signup attempts (expected 401 for bad credentials)
      if (!path.includes('/auth/login') && !path.includes('/auth/signup')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);

// Extract a user-friendly error message from an axios error
const getErrorMessage = (error, fallback) => {
  if (error._isNetworkError) {
    return 'Unable to connect. Please check your internet connection.';
  }
  return error.response?.data?.error || fallback;
};

// ============================================
// AUTHENTICATION
// ============================================

export const signup = async (email, password, name) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/signup`, {
      email,
      password,
      name
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to sign up'));
  }
};

export const login = async (email, password) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/login`, {
      email,
      password
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to login'));
  }
};

export const forgotPassword = async (email) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/forgot-password`, { email });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to send reset email'));
  }
};

export const resetPassword = async (token, newPassword) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/reset-password`, { token, newPassword });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to reset password'));
  }
};

export const updateProfile = async (data) => {
  try {
    const response = await axios.patch(`${API_BASE_URL}/auth/profile`, data, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update profile'));
  }
};

export const changePassword = async (data) => {
  try {
    const response = await axios.patch(`${API_BASE_URL}/auth/password`, data, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to change password'));
  }
};

export const getMe = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/auth/me`, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch user'));
  }
};

// ============================================
// LEAGUES
// ============================================

export const createLeague = async (leagueData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/leagues`,
      leagueData,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to create league'));
  }
};

export const getLeagues = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/leagues`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch leagues'));
  }
};

export const getLeague = async (leagueId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/leagues/${leagueId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch league'));
  }
};

export const updateLeague = async (leagueId, updates) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/leagues/${leagueId}`,
      updates,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update league'));
  }
};

export const checkSlugAvailability = async (slug, leagueId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/leagues/check-slug/${encodeURIComponent(slug)}`,
      { params: { leagueId }, headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to check slug'));
  }
};

export const deleteLeague = async (leagueId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/leagues/${leagueId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to delete league'));
  }
};

// ============================================
// PUBLIC PAGES
// ============================================

export const getPublicLeague = async (slug) => {
  try {
    const response = await axios.get(`${SERVER_BASE_URL}/public/${slug}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch public league'));
  }
};

export const getPublicStandings = async (slug) => {
  try {
    const response = await axios.get(`${SERVER_BASE_URL}/public/${slug}/standings`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch standings'));
  }
};

// ============================================
// ADMIN
// ============================================

export const getAdminUsers = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/users`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch admin data'));
  }
};

export const getAdminLeague = async (leagueId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/leagues/${leagueId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch league'));
  }
};

export const updateAdminLeague = async (leagueId, updates) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/admin/leagues/${leagueId}`,
      updates,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update league'));
  }
};

export const updateUserPlan = async (userId, plan, { compedUntil, compReason } = {}) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/admin/users/${userId}/plan`,
      { plan, compedUntil, compReason },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update plan'));
  }
};

export const adminResetPassword = async (userId) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/admin/users/${userId}/reset-password`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to send reset link'));
  }
};

export const adminCreateUser = async (userData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/admin/users`,
      userData,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to create user'));
  }
};

export const adminUpdateUser = async (userId, updates) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/admin/users/${userId}`,
      updates,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update user'));
  }
};

export const adminDeleteUser = async (userId, options = {}) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/admin/users/${userId}`,
      { headers: getAuthHeaders(), data: options }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to delete user'));
  }
};

export const adminSetPreviewTier = async (tier) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/admin/preview-tier`,
      { tier },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to set preview tier'));
  }
};

export const deleteAdminLeague = async (leagueId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/admin/leagues/${leagueId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to delete league'));
  }
};

export const adminExportLeague = async (leagueId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/leagues/${leagueId}/export`,
      { headers: getAuthHeaders(), responseType: 'blob' }
    );
    return response;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to export league'));
  }
};

export const getAdminAnalytics = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/analytics`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch analytics'));
  }
};

export const getAdminLeagues = async (params = {}) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/leagues`,
      { params, headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch admin leagues'));
  }
};

export const reassignLeague = async (leagueId, newUserId) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/admin/leagues/${leagueId}/reassign`,
      { newUserId },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to reassign league'));
  }
};

// ============================================
// ADMIN MIGRATIONS
// ============================================

export const previewAccountMigration = async (sourceUserId, targetUserId, deleteSourceAfter) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/admin/migrate/preview-account`,
      { sourceUserId, targetUserId, deleteSourceAfter },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to preview migration'));
  }
};

export const executeAccountMigration = async (sourceUserId, targetUserId, deleteSourceAfter) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/admin/migrate/account`,
      { sourceUserId, targetUserId, deleteSourceAfter },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to execute migration'));
  }
};

export const previewLeagueMigration = async (leagueId, targetUserId) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/admin/migrate/preview-league`,
      { leagueId, targetUserId },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to preview league migration'));
  }
};

export const executeLeagueMigration = async (leagueId, targetUserId) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/admin/migrate/league`,
      { leagueId, targetUserId },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to execute league migration'));
  }
};

export const getMigrationLogs = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/admin/migrations`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch migration logs'));
  }
};

// ============================================
// PLAYOFFS
// ============================================

export const getPlayoffs = async (leagueId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/leagues/${leagueId}/playoffs`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch playoffs'));
  }
};

export const generatePlayoffs = async (leagueId, rounds) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/leagues/${leagueId}/playoffs`,
      { rounds },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to generate playoffs'));
  }
};

export const savePlayoffScore = async (leagueId, gameId, team1Score, team2Score) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/leagues/${leagueId}/playoffs/games/${gameId}/score`,
      { team1Score, team2Score },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to save playoff score'));
  }
};

export const deletePlayoffs = async (leagueId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/leagues/${leagueId}/playoffs`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to reset playoffs'));
  }
};

export const applyPlayoffOverride = async (leagueId, gameId, winnerId, reason) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/leagues/${leagueId}/playoffs/games/${gameId}/override`,
      { winnerId, reason },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to apply override'));
  }
};

export const clearPlayoffOverride = async (leagueId, gameId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/leagues/${leagueId}/playoffs/games/${gameId}/override`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to clear override'));
  }
};

// ============================================
// SCORES
// ============================================

export const updateGameScore = async (leagueId, gameId, team1Score, team2Score) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/leagues/${leagueId}/games/${gameId}/score`,
      { team1Score, team2Score },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update score'));
  }
};

export const bulkScoreEntry = async (leagueId, scores) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/leagues/${leagueId}/games/bulk-score`,
      { scores },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to bulk score games'));
  }
};

export const updateGame = async (leagueId, gameId, updates) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/leagues/${leagueId}/games/${gameId}`,
      updates,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update game'));
  }
};

export const swapGames = async (leagueId, gameId1, gameId2) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/leagues/${leagueId}/games/swap`,
      { gameId1, gameId2 },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to swap games'));
  }
};

export const updateGameStatus = async (leagueId, gameId, data) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/leagues/${leagueId}/games/${gameId}/status`,
      data,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update game status'));
  }
};

// ============================================
// SCHEDULE GENERATION (Legacy - for PDF only now)
// ============================================

export const generateSchedule = async (leagueData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/schedule/generate`, leagueData);
    return response.data;
  } catch (error) {
    throw new Error(error._isNetworkError ? 'Unable to connect. Please check your internet connection.' : (error.response?.data?.errors?.[0] || 'Failed to generate schedule'));
  }
};

export const generatePDF = async (leagueData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/schedule/generate-pdf`,
      leagueData,
      { responseType: 'blob' }
    );
    return response.data;
  } catch (error) {
    throw new Error('Failed to generate PDF');
  }
};

export const generatePreviewPDF = async (leagueData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/schedule/preview-pdf`,
      leagueData,
      { responseType: 'blob' }
    );
    return response.data;
  } catch (error) {
    throw new Error('Failed to generate preview PDF');
  }
};

// Helper function to trigger file download
export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);

  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// ============================================
// NOTIFICATIONS
// ============================================

export const notifyCaptains = async (leagueId, type) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/leagues/${leagueId}/notify`,
      { type },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to send notifications'));
  }
};

// ============================================
// CONTACT
// ============================================

export const sendContact = async (name, email, message) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/contact`,
      { name, email, message },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to send message'));
  }
};

export const sendBetaRequest = async (name, email, organization, description) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/contact/beta-request`,
      { name, email, organization, description }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to send beta request'));
  }
};

// ============================================
// CAPTAIN PORTAL
// ============================================

export const verifyCaptainToken = async (token) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/captain/verify/${token}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Invalid or expired link'));
  }
};

export const getCaptainSchedule = async (token) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/captain/${token}/schedule`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to load schedule'));
  }
};

export const getCaptainStandings = async (token) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/captain/${token}/standings`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to load standings'));
  }
};

export const updateCaptainInfo = async (token, { captainName, captainEmail }) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/captain/${token}/info`,
      { captainName, captainEmail }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update info'));
  }
};

export const submitCaptainScore = async (token, gameId, { team1Score, team2Score }) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/captain/${token}/games/${gameId}/score`,
      { team1Score, team2Score }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to submit score'));
  }
};

export const getPendingScores = async (leagueId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/leagues/${leagueId}/pending-scores`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch pending scores'));
  }
};

export const reviewPendingScore = async (leagueId, pendingId, status) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/leagues/${leagueId}/pending-scores/${pendingId}`,
      { status },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to review score'));
  }
};

export const generateCaptainLinks = async (leagueId) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/leagues/${leagueId}/captain-links`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to generate captain links'));
  }
};

// ============================================
// SEASONS
// ============================================

export const archiveSeason = async (leagueId, name) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/leagues/${leagueId}/archive-season`,
      { name },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to archive season'));
  }
};

export const getSeasons = async (leagueId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/leagues/${leagueId}/seasons`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch seasons'));
  }
};

export const getSeason = async (leagueId, seasonId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/leagues/${leagueId}/seasons/${seasonId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch season'));
  }
};

export const getPublicSeason = async (slug, seasonId) => {
  try {
    const response = await axios.get(
      `${SERVER_BASE_URL}/public/${slug}/seasons/${seasonId}`
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch season'));
  }
};

// ============================================
// VENUES
// ============================================

export const getVenues = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/venues`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch venues'));
  }
};

export const createVenue = async (venueData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/venues`,
      venueData,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to create venue'));
  }
};

export const updateVenue = async (venueId, updates) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/venues/${venueId}`,
      updates,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update venue'));
  }
};

export const deleteVenue = async (venueId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/venues/${venueId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to delete venue'));
  }
};

export const assignVenueToGame = async (leagueId, gameId, venueId) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/leagues/${leagueId}/games/${gameId}/venue`,
      { venueId: venueId || null },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to assign venue'));
  }
};

// ============================================
// REFEREES
// ============================================

export const getReferees = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/referees`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch referees'));
  }
};

export const createReferee = async (data) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/referees`,
      data,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to create referee'));
  }
};

export const updateReferee = async (refId, updates) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/referees/${refId}`,
      updates,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update referee'));
  }
};

export const deleteReferee = async (refId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/referees/${refId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to delete referee'));
  }
};

export const generateRefereeToken = async (refId) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/referees/${refId}/token`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to generate referee link'));
  }
};

export const assignRefereeToGame = async (leagueId, gameId, refereeId) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/leagues/${leagueId}/games/${gameId}/referee`,
      { refereeId: refereeId || null },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to assign referee'));
  }
};

export const getRefereeSchedule = async (token) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/referee/${token}/schedule`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Invalid or expired link'));
  }
};

export const submitRefereeScore = async (token, gameId, { team1Score, team2Score }) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/referee/${token}/games/${gameId}/score`,
      { team1Score, team2Score }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to submit score'));
  }
};

// ============================================
// CAPTAINS (user-scoped CRUD)
// ============================================

export const getCaptains = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/captains`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch captains'));
  }
};

export const createCaptain = async (data) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/captains`,
      data,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to create captain'));
  }
};

export const updateCaptain = async (id, updates) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/captains/${id}`,
      updates,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update captain'));
  }
};

export const deleteCaptain = async (id) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/captains/${id}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to delete captain'));
  }
};

// ============================================
// LEAGUE IMPORT
// ============================================

export const importLeague = async (jsonData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/leagues/import`,
      jsonData,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    if (error.response?.data?.errors) {
      const err = new Error('Validation failed');
      err.validationErrors = error.response.data.errors;
      throw err;
    }
    throw new Error(getErrorMessage(error, 'Failed to import league'));
  }
};

export const getImportTemplate = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/leagues/import-template`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch import template'));
  }
};

export const exportLeague = async (leagueId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/leagues/${leagueId}/export`,
      { headers: getAuthHeaders(), responseType: 'blob' }
    );
    return response;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to export league'));
  }
};

export const duplicateLeague = async (leagueId) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/leagues/${leagueId}/duplicate`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to duplicate league'));
  }
};

// Default export for compatibility
// ============================================
// REGISTRATION FORMS
// ============================================

export const getRegistrationForm = async (leagueId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/leagues/${leagueId}/registration-form`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch registration form'));
  }
};

export const createRegistrationForm = async (leagueId, formData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/leagues/${leagueId}/registration-form`,
      formData,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to create registration form'));
  }
};

export const updateRegistrationForm = async (leagueId, updates) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/leagues/${leagueId}/registration-form`,
      updates,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update registration form'));
  }
};

export const updateRegistrationFormStatus = async (leagueId, status) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/leagues/${leagueId}/registration-form/status`,
      { status },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update registration form status'));
  }
};

export const addRegistrationField = async (leagueId, fieldData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/leagues/${leagueId}/registration-form/fields`,
      fieldData,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to add registration field'));
  }
};

export const updateRegistrationField = async (leagueId, fieldId, updates) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/leagues/${leagueId}/registration-form/fields/${fieldId}`,
      updates,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update registration field'));
  }
};

export const deleteRegistrationField = async (leagueId, fieldId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/leagues/${leagueId}/registration-form/fields/${fieldId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to delete registration field'));
  }
};

// ---------------------------------------------------------------------------
// Public Registration (unauthenticated)
// ---------------------------------------------------------------------------

export const getPublicRegistrationForm = async (leagueId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/register/${leagueId}`);
    return response.data;
  } catch (error) {
    // Re-throw original axios error so callers can check response.status and response.data.status
    throw error;
  }
};

export const getRegistrationTeams = async (leagueId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/register/${leagueId}/teams`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch teams'));
  }
};

export const checkRegistrantEmail = async (leagueId, email) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/register/${leagueId}/check-email`, { params: { email } });
    return response.data;
  } catch {
    return { found: false };
  }
};

export const submitRegistration = async (leagueId, registrationData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/register/${leagueId}`, registrationData);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to submit registration'));
  }
};

export const sendPaymentLink = async (leagueId, registrationId) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/leagues/${leagueId}/registrations/${registrationId}/send-payment-link`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to send payment link'));
  }
};

// ============================================
// ROSTER MANAGEMENT
// ============================================

export const getRoster = async (leagueId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/leagues/${leagueId}/roster`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch roster'));
  }
};

export const getTeamRoster = async (leagueId, teamId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/leagues/${leagueId}/teams/${teamId}/roster`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch team roster'));
  }
};

export const moveRosterPlayer = async (leagueId, registrationId, toTeamId) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/leagues/${leagueId}/roster/move`,
      { registrationId, toTeamId },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to move player'));
  }
};

export const addRosterPlayer = async (leagueId, playerData) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/leagues/${leagueId}/roster/add`,
      playerData,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to add player'));
  }
};

export const removeRosterPlayer = async (leagueId, registrationId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/leagues/${leagueId}/roster/${registrationId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to remove player'));
  }
};

// ============================================
// LEAGUE WEBSITE CONFIG
// ============================================

export const getWebsite = async (leagueId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/leagues/${leagueId}/website`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch website config'));
  }
};

export const updateWebsite = async (leagueId, updates) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/leagues/${leagueId}/website`,
      updates,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update website config'));
  }
};

export const checkSubdomainAvailability = async (subdomain) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/subdomain/check/${encodeURIComponent(subdomain)}`);
    return response.data; // { available: boolean, error?: string }
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to check subdomain availability'));
  }
};

export const claimSubdomain = async (leagueId, subdomain) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/leagues/${leagueId}/website/subdomain`,
      { subdomain },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to claim subdomain'));
  }
};

export const changeSubdomain = async (leagueId, subdomain) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/leagues/${leagueId}/website/subdomain`,
      { subdomain },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to change subdomain'));
  }
};

export const releaseSubdomain = async (leagueId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/leagues/${leagueId}/website/subdomain`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to release subdomain'));
  }
};

export const setupCustomDomain = async (leagueId, domain) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/leagues/${leagueId}/website/custom-domain`,
      { domain },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to set up custom domain'));
  }
};

export const getCustomDomainStatus = async (leagueId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/leagues/${leagueId}/website/custom-domain/status`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to check custom domain status'));
  }
};

export const deleteCustomDomain = async (leagueId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/leagues/${leagueId}/website/custom-domain`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to remove custom domain'));
  }
};

export const getAnnouncements = async (leagueId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/leagues/${leagueId}/announcements`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch announcements'));
  }
};

export const createAnnouncement = async (leagueId, data) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/leagues/${leagueId}/announcements`,
      data,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to create announcement'));
  }
};

export const updateAnnouncement = async (leagueId, id, data) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/leagues/${leagueId}/announcements/${id}`,
      data,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update announcement'));
  }
};

export const deleteAnnouncement = async (leagueId, id) => {
  try {
    await axios.delete(
      `${API_BASE_URL}/leagues/${leagueId}/announcements/${id}`,
      { headers: getAuthHeaders() }
    );
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to delete announcement'));
  }
};

export const getSponsors = async (leagueId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/leagues/${leagueId}/sponsors`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch sponsors'));
  }
};

export const createSponsor = async (leagueId, data) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/leagues/${leagueId}/sponsors`,
      data,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to create sponsor'));
  }
};

export const updateSponsor = async (leagueId, id, data) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/leagues/${leagueId}/sponsors/${id}`,
      data,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update sponsor'));
  }
};

export const deleteSponsor = async (leagueId, id) => {
  try {
    await axios.delete(
      `${API_BASE_URL}/leagues/${leagueId}/sponsors/${id}`,
      { headers: getAuthHeaders() }
    );
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to delete sponsor'));
  }
};

// ============================================
// IMAGE UPLOADS
// ============================================

export const uploadImage = async (file, category) => {
  try {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('category', category);

    const response = await axios.post(
      `${API_BASE_URL}/uploads/image`,
      formData,
      {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data; // { url, key }
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to upload image'));
  }
};

export const deleteUploadedImage = async (key) => {
  try {
    await axios.delete(`${API_BASE_URL}/uploads/image`, {
      headers: getAuthHeaders(),
      data: { key },
    });
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to delete image'));
  }
};

// ============================================
// LEAGUE PHOTOS
// ============================================

export const getPhotos = async (leagueId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/leagues/${leagueId}/photos`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch photos'));
  }
};

export const addPhoto = async (leagueId, data) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/leagues/${leagueId}/photos`,
      data,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to add photo'));
  }
};

export const updatePhoto = async (leagueId, id, data) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/leagues/${leagueId}/photos/${id}`,
      data,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update photo'));
  }
};

export const deletePhoto = async (leagueId, id) => {
  try {
    await axios.delete(
      `${API_BASE_URL}/leagues/${leagueId}/photos/${id}`,
      { headers: getAuthHeaders() }
    );
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to delete photo'));
  }
};

export const getPublicWebsite = async (identifier) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/public/website/${identifier}`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'League website not found'));
  }
};

export const getPublicWebsiteByHostname = async (hostname) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/public/website/by-hostname`, {
      params: { hostname }
    });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'League website not found'));
  }
};

export const getPublicRoster = async (slug) => {
  try {
    const response = await axios.get(`${API_BASE_URL.replace('/api', '')}/public/${slug}/roster`);
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch public roster'));
  }
};

export const refundRegistration = async (leagueId, registrationId) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/leagues/${leagueId}/registrations/${registrationId}/refund`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to process refund'));
  }
};

export const createCheckoutSession = async (leagueId, registrationId) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/register/${leagueId}/checkout-session`, { registrationId });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to create payment session'));
  }
};

export const reorderRegistrationFields = async (leagueId, fieldIds) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/leagues/${leagueId}/registration-form/fields/reorder`,
      { fieldIds },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to reorder registration fields'));
  }
};

export const getRegistrations = async (leagueId, params = {}) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/leagues/${leagueId}/registrations`,
      { headers: getAuthHeaders(), params }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to load registrations'));
  }
};

export const updateRegistration = async (leagueId, registrationId, updates) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/leagues/${leagueId}/registrations/${registrationId}`,
      updates,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update registration'));
  }
};

export const deleteRegistration = async (leagueId, registrationId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/leagues/${leagueId}/registrations/${registrationId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to delete registration'));
  }
};

export const exportRegistrations = async (leagueId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/leagues/${leagueId}/registrations/export`,
      { headers: getAuthHeaders(), responseType: 'blob' }
    );
    return response;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to export registrations'));
  }
};

// ============================================
// STRIPE CONNECT
// ============================================

export const connectStripe = async () => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/stripe/connect`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to start Stripe onboarding'));
  }
};

export const getStripeStatus = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/stripe/connect/status`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch Stripe status'));
  }
};

export const getStripeDashboardLink = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/stripe/connect/dashboard`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to get Stripe Dashboard link'));
  }
};

// ============================================
// STRIPE SUBSCRIPTION MANAGEMENT
// ============================================

export const getSubscriptionDetails = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/stripe/subscription`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch subscription details'));
  }
};

export const createSubscriptionCheckout = async (plan) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/stripe/create-checkout-session`,
      { plan },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to create checkout session'));
  }
};

export const createPortalSession = async () => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/stripe/create-portal-session`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to create portal session'));
  }
};

export const checkDowngrade = async (targetPlan) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/stripe/check-downgrade`,
      { targetPlan },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to check downgrade eligibility'));
  }
};

export const reactivateSubscription = async () => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/stripe/reactivate`,
      {},
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to reactivate subscription'));
  }
};

// ============================================
// BLOG (defined here to avoid use-before-define)
// ============================================

export const getBlogArticles = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/blog`);
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to fetch articles');
  }
};

export const getBlogArticle = async (slug) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/blog/${slug}`);
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Article not found');
  }
};

export const getAdminBlogArticles = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/blog?all=true`, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to fetch articles');
  }
};

export const createBlogArticle = async (data) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/blog`, data, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to create article');
  }
};

export const updateBlogArticle = async (id, data) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/blog/${id}`, data, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to update article');
  }
};

export const deleteBlogArticle = async (id) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/blog/${id}`, {
      headers: getAuthHeaders()
    });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to delete article');
  }
};

const api = {
  get: (url, config) => axios.get(`${API_BASE_URL}${url}`, { ...config, headers: { ...getAuthHeaders(), ...config?.headers } }),
  post: (url, data, config) => axios.post(`${API_BASE_URL}${url}`, data, { ...config, headers: { ...getAuthHeaders(), ...config?.headers } }),
  put: (url, data, config) => axios.put(`${API_BASE_URL}${url}`, data, { ...config, headers: { ...getAuthHeaders(), ...config?.headers } }),
  delete: (url, config) => axios.delete(`${API_BASE_URL}${url}`, { ...config, headers: { ...getAuthHeaders(), ...config?.headers } }),
  signup,
  login,
  forgotPassword,
  resetPassword,
  updateProfile,
  changePassword,
  getMe,
  createLeague,
  getLeagues,
  getLeague,
  updateLeague,
  deleteLeague,
  getPublicLeague,
  getPublicStandings,
  getAdminUsers,
  getAdminLeague,
  updateAdminLeague,
  updateUserPlan,
  adminResetPassword,
  adminCreateUser,
  adminUpdateUser,
  adminDeleteUser,
  adminSetPreviewTier,
  deleteAdminLeague,
  adminExportLeague,
  getAdminAnalytics,
  updateGameScore,
  updateGame,
  swapGames,
  getPlayoffs,
  generatePlayoffs,
  savePlayoffScore,
  deletePlayoffs,
  generateSchedule,
  generatePDF,
  generatePreviewPDF,
  downloadFile,
  sendContact,
  sendBetaRequest,
  notifyCaptains,
  verifyCaptainToken,
  getCaptainSchedule,
  getCaptainStandings,
  updateCaptainInfo,
  submitCaptainScore,
  getPendingScores,
  reviewPendingScore,
  generateCaptainLinks,
  archiveSeason,
  getSeasons,
  getSeason,
  getPublicSeason,
  getVenues,
  createVenue,
  updateVenue,
  deleteVenue,
  assignVenueToGame,
  getReferees,
  createReferee,
  updateReferee,
  deleteReferee,
  generateRefereeToken,
  assignRefereeToGame,
  getRefereeSchedule,
  submitRefereeScore,
  importLeague,
  getImportTemplate,
  exportLeague,
  duplicateLeague,
  previewAccountMigration,
  executeAccountMigration,
  previewLeagueMigration,
  executeLeagueMigration,
  getMigrationLogs,
  getRegistrationForm,
  createRegistrationForm,
  updateRegistrationForm,
  updateRegistrationFormStatus,
  addRegistrationField,
  updateRegistrationField,
  deleteRegistrationField,
  reorderRegistrationFields,
  getPublicRegistrationForm,
  getRegistrationTeams,
  checkRegistrantEmail,
  submitRegistration,
  getRegistrations,
  updateRegistration,
  deleteRegistration,
  exportRegistrations,
  connectStripe,
  getStripeStatus,
  getStripeDashboardLink,
  createCheckoutSession,
  sendPaymentLink,
  refundRegistration,
  getRoster,
  getTeamRoster,
  moveRosterPlayer,
  addRosterPlayer,
  removeRosterPlayer,
  getPublicRoster,
  // Website config
  getWebsite,
  updateWebsite,
  checkSubdomainAvailability,
  claimSubdomain,
  changeSubdomain,
  releaseSubdomain,
  getAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
  getSponsors,
  createSponsor,
  updateSponsor,
  deleteSponsor,
  getPublicWebsite,
  // Image uploads
  uploadImage,
  deleteUploadedImage,
  // League photos
  getPhotos,
  addPhoto,
  updatePhoto,
  deletePhoto,
  // Blog
  getBlogArticles,
  getBlogArticle,
  getAdminBlogArticles,
  createBlogArticle,
  updateBlogArticle,
  deleteBlogArticle,
};

export default api;

// ---------------------------------------------------------------------------
// Tournament authenticated management
// ---------------------------------------------------------------------------

export const getTournaments = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/tournaments`, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to fetch tournaments');
  }
};

export const createTournament = async (data) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/tournaments`, data, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to create tournament');
  }
};

export const getTournament = async (id) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/tournaments/${id}`, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to fetch tournament');
  }
};

export const updateTournament = async (id, data) => {
  try {
    const response = await axios.patch(`${API_BASE_URL}/tournaments/${id}`, data, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to update tournament');
  }
};

export const deleteTournament = async (id) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/tournaments/${id}`, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to delete tournament');
  }
};

export const updateTournamentStatus = async (id, status) => {
  try {
    const response = await axios.patch(`${API_BASE_URL}/tournaments/${id}/status`, { status }, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to update tournament status');
  }
};

export const addTournamentTeam = async (id, data) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/tournaments/${id}/teams`, data, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to add team');
  }
};

export const updateTournamentTeam = async (id, teamId, data) => {
  try {
    const response = await axios.patch(`${API_BASE_URL}/tournaments/${id}/teams/${teamId}`, data, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to update team');
  }
};

export const deleteTournamentTeam = async (id, teamId) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/tournaments/${id}/teams/${teamId}`, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to remove team');
  }
};

export const seedTournamentTeams = async (id, method) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/tournaments/${id}/teams/seed`, { method }, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to seed teams');
  }
};

export const assignTournamentPools = async (id, numPools) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/tournaments/${id}/teams/assign-pools`, { numPools }, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to assign pools');
  }
};

export const generateTournamentBracket = async (id) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/tournaments/${id}/generate-bracket`, {}, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to generate bracket');
  }
};

export const scoreTournamentMatch = async (id, matchId, homeScore, awayScore) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/tournaments/${id}/matches/${matchId}/score`,
      { homeScore, awayScore },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to score match');
  }
};

export const getTournamentStandings = async (id) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/tournaments/${id}/standings`, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to fetch standings');
  }
};

export const advanceTournamentGroupWinners = async (id) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/tournaments/${id}/advance-group-winners`, {}, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to advance group winners');
  }
};

export const resetTournamentBracket = async (id) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/tournaments/${id}/bracket`, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to reset bracket');
  }
};

// ---------------------------------------------------------------------------
// Tournament notifications
// ---------------------------------------------------------------------------

export const sendTournamentReminder = async (id) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/tournaments/${id}/notify/reminder`, {}, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to send reminders');
  }
};

export const sendTournamentAnnouncement = async (id, message) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/tournaments/${id}/notify/announcement`, { message }, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to send announcement');
  }
};

// ---------------------------------------------------------------------------
// Tournament check-in
// ---------------------------------------------------------------------------

export const checkInTournamentTeam = async (id, teamId) => {
  try {
    const response = await axios.patch(`${API_BASE_URL}/tournaments/${id}/teams/${teamId}/check-in`, {}, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to check in team');
  }
};

export const checkInAllTournamentTeams = async (id) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/tournaments/${id}/teams/check-in-all`, {}, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to bulk check-in teams');
  }
};

// ---------------------------------------------------------------------------
// Tournament public registration (QR / direct link)
// ---------------------------------------------------------------------------

export const getTournamentJoinInfo = async (joinSlug) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/join/t/${joinSlug}`);
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to load tournament');
  }
};

export const submitTournamentRegistration = async (joinSlug, registrationData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/join/t/${joinSlug}`, registrationData);
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to submit registration');
  }
};

// Public tournament info (unauthenticated)
export const getPublicTournamentInfo = async (joinSlug) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/public/tournaments/${joinSlug}`);
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Tournament not found');
  }
};

// Public bracket data (unauthenticated)
export const getPublicTournamentBracket = async (joinSlug) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/public/tournaments/${joinSlug}/bracket`);
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to load bracket');
  }
};

// ---------------------------------------------------------------------------
// Pickleball player ratings
// ---------------------------------------------------------------------------

export const getPickleballRatings = async (leagueId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/leagues/${leagueId}/pickleball/ratings`, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to load ratings');
  }
};

export const getPickleballPlayerRating = async (leagueId, email) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/leagues/${leagueId}/pickleball/ratings/${encodeURIComponent(email)}`, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to load player rating');
  }
};

export const addPickleballPlayer = async (leagueId, data) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/leagues/${leagueId}/pickleball/ratings`, data, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to add player');
  }
};

export const updatePickleballRating = async (leagueId, email, data) => {
  try {
    const response = await axios.patch(`${API_BASE_URL}/leagues/${leagueId}/pickleball/ratings/${encodeURIComponent(email)}`, data, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to update rating');
  }
};

export const deletePickleballRating = async (leagueId, email) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/leagues/${leagueId}/pickleball/ratings/${encodeURIComponent(email)}`, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to remove player');
  }
};

export const recalculatePickleballRatings = async (leagueId) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/leagues/${leagueId}/pickleball/ratings/recalculate`, {}, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to recalculate ratings');
  }
};

// ============================================
// ROTATING PARTNER LEAGUE PLAYERS
// ============================================

export const getLeaguePlayers = async (leagueId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/leagues/${leagueId}/players`, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to fetch players');
  }
};

export const addLeaguePlayer = async (leagueId, playerData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/leagues/${leagueId}/players`, playerData, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to add player');
  }
};

export const updateLeaguePlayer = async (leagueId, playerId, data) => {
  try {
    const response = await axios.patch(`${API_BASE_URL}/leagues/${leagueId}/players/${playerId}`, data, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to update player');
  }
};

export const deleteLeaguePlayer = async (leagueId, playerId) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/leagues/${leagueId}/players/${playerId}`, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to remove player');
  }
};

export const generateRotatingSchedule = async (leagueId, data) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/leagues/${leagueId}/players/generate-schedule`, data, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to generate schedule');
  }
};

export const deleteRotatingSchedule = async (leagueId) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/leagues/${leagueId}/players/schedule`, { headers: getAuthHeaders() });
    return response.data;
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Failed to clear schedule');
  }
};

// ============================================
// ADMIN SPORTS CONFIGURATION
// ============================================

export const getAdminSports = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/admin/sports`, { headers: getAuthHeaders() });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to fetch sports'));
  }
};

export const createAdminSport = async (data) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/admin/sports`, data, { headers: getAuthHeaders() });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to create sport'));
  }
};

export const updateAdminSport = async (id, data) => {
  try {
    const response = await axios.patch(`${API_BASE_URL}/admin/sports/${id}`, data, { headers: getAuthHeaders() });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to update sport'));
  }
};

export const deleteAdminSport = async (id) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/admin/sports/${id}`, { headers: getAuthHeaders() });
    return response.data;
  } catch (error) {
    throw new Error(getErrorMessage(error, 'Failed to delete sport'));
  }
};

